import React, { useCallback, useEffect, useMemo, useState } from 'react';
import BucketsLoading from './BucketsLoading';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import { api } from '../../../app/http-common';
import { activeSuggestions, chipColor, initials } from '../constants';
import { orderClientsForPicker } from '../hooks/useBucketData';
import AppModal from '../../../components/AppModal/AppModal';

// §1.6 + §1.7 — the destination picker.
//
// Was: every publisher permanently expanded, no ordering, selecting a bucket
// fired Move/Copy IMMEDIATELY, and no way to see what was in a destination.
//
// Now:
//   * Publisher groups start COLLAPSED. Click the row to expand.
//   * Ordering matches Publisher Buckets — favourites first, then Recent
//     activity or A–Z (read from the same localStorage key that page uses).
//   * The confirm sits in a persistent footer that stays visible while the
//     destination list scrolls.
//   * A Copy/Move toggle lives in that footer. Copy is preselected — a
//     destructive Move is always an explicit choice.
//   * Search expands ONLY publishers whose name or bucket names match, so the
//     rest of the list stays quiet.
//   * "Preview bucket" per destination row swaps the list in place for a
//     read-only view of that bucket's active suggestions and back, without
//     losing search, expansion, destination or Copy/Move choice.
//
// Move is only offered when every incoming story is a Suggestion — the
// existing rule the caller enforces. This modal exposes the toggle regardless
// so a mixed selection can still see the mode chip and just get Copy disabled.
export default function MoveToModal({
  isOpen, mode: initialMode, count, clients, currentBucketId,
  incoming = [], onSelect, onClose, loading = false,
}) {
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState(() => new Set());
  const [selectedBucket, setSelectedBucket] = useState(null);
  // §1.5 — Copy is the default operation regardless of how the picker opened.
  // The caller may still hint 'move' by initialMode; honour that only when
  // every incoming story is a Suggestion (i.e. Move is genuinely available).
  const allSuggestions = incoming.length > 0 && incoming.every((it) => it.status === 'suggestion');
  const [mode, setMode] = useState(initialMode === 'move' && allSuggestions ? 'move' : 'copy');
  const [preview, setPreview] = useState(null);   // { bucket, items, loading, error }

  // Reset on open so a stale prior selection or Preview does not linger.
  useEffect(() => {
    if (!isOpen) return;
    setQuery('');
    setExpanded(new Set());
    setSelectedBucket(null);
    setPreview(null);
    setMode(initialMode === 'move' && allSuggestions ? 'move' : 'copy');
  }, [isOpen, initialMode, allSuggestions]);

  // §1.6 — same order as Publisher Buckets: favourites first, then the sort
  // the user chose there. Read directly rather than lifting state, because the
  // two screens do not share a store and duplicating the setter would drift.
  const orderedClients = useMemo(() => orderClientsForPicker(clients), [clients]);

  // Search: match publisher or bucket name (§1.6).
  const filteredTree = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return orderedClients.map((c) => ({ client: c, matchedBuckets: c.buckets }));
    return orderedClients
      .map((c) => {
        const clientHit = c.clientName.toLowerCase().includes(q);
        const matchedBuckets = clientHit
          ? c.buckets
          : c.buckets.filter((b) => (b.name || '').toLowerCase().includes(q));
        if (!clientHit && matchedBuckets.length === 0) return null;
        return { client: c, matchedBuckets };
      })
      .filter(Boolean);
  }, [orderedClients, query]);

  // §1.6 — search auto-expands ONLY the matching publisher groups.
  useEffect(() => {
    if (!query.trim()) return;
    const matchingIds = new Set(filteredTree.map((n) => n.client.clientId));
    setExpanded(matchingIds);
  }, [query, filteredTree]);

  const toggleClient = useCallback((clientId) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(clientId)) next.delete(clientId); else next.add(clientId);
      return next;
    });
  }, []);

  // §1.7 — open the read-only preview.
  const openPreview = useCallback(async (bucket) => {
    setPreview({ bucket, items: null, loading: true, error: null });
    try {
      const res = await api.getBucketStories(bucket._id, {
        status: 'suggestion', limit: 30, include_archived: 'hide',
      });
      setPreview({ bucket, items: res.items || [], loading: false, error: null });
    } catch (err) {
      setPreview({
        bucket, items: null, loading: false,
        error: err?.response?.data?.error || 'Could not load this bucket.',
      });
    }
  }, []);

  const closePreview = useCallback(() => setPreview(null), []);

  const commit = useCallback(() => {
    if (!selectedBucket) return;
    // Guard against a mixed selection sneaking Move through: the parent still
    // enforces this too, but the picker should never send an impossible request.
    const effectiveMode = mode === 'move' && !allSuggestions ? 'copy' : mode;
    onSelect(selectedBucket, effectiveMode);
  }, [selectedBucket, mode, allSuggestions, onSelect]);

  return (
    <AppModal
      isOpen={isOpen}
      title={preview ? `Preview: ${preview.bucket.name}` : 'Send to another bucket'}
      onCancel={onClose}
      maxWidth={620}
      footer={(
        // §1.6 — persistent footer. Stays visible while the list scrolls.
        <div className="pbnew-move-footer">
          {preview ? (
            <>
              <button className="pbnew-btn" onClick={closePreview}>
                <ArrowBackOutlinedIcon style={{ fontSize: 15 }} /> Back to destinations
              </button>
              <span className="pbnew-move-note">
                {preview.bucket.name} — read-only preview
              </span>
            </>
          ) : (
            <>
              <div className="pbnew-move-mode" role="group" aria-label="Operation">
                {/* §1.5 — Copy is the primary, filled action. Move is disabled
                    when any incoming story is not a Suggestion. */}
                <button
                  type="button"
                  className={`pbnew-move-mode-btn ${mode === 'copy' ? 'on' : ''}`}
                  onClick={() => setMode('copy')}
                  aria-pressed={mode === 'copy'}
                >
                  Copy
                </button>
                <button
                  type="button"
                  className={`pbnew-move-mode-btn ${mode === 'move' ? 'on' : ''}`}
                  onClick={() => setMode('move')}
                  disabled={!allSuggestions}
                  title={allSuggestions ? 'Move — removes from this bucket' : 'Move is available only when every selected story is a Suggestion'}
                  aria-pressed={mode === 'move'}
                >
                  Move
                </button>
              </div>
              <div className="pbnew-move-footer-right">
                <button className="pbnew-btn" onClick={onClose}>Cancel</button>
                <button
                  type="button"
                  className="pbnew-btn primary"
                  disabled={!selectedBucket}
                  onClick={commit}
                >
                  {mode === 'move' ? 'Move' : 'Copy'} {count} to{' '}
                  {selectedBucket ? selectedBucket.name : '…'}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    >
      <div className="pbnew-scope">
        {preview ? (
          <div className="pbnew-move-preview">
            {preview.loading && (
              <div className="pbnew-drop-hint" style={{ padding: 16 }}>Loading…</div>
            )}
            {preview.error && (
              <div className="pbnew-drop-hint error" style={{ padding: 16 }}>{preview.error}</div>
            )}
            {preview.items && preview.items.length === 0 && (
              <div className="pbnew-drop-hint" style={{ padding: 16 }}>
                No active suggestions in this bucket.
              </div>
            )}
            {preview.items && preview.items.length > 0 && (
              <>
                <div className="pbnew-move-preview-head">
                  Active suggestions ({preview.items.length}
                  {preview.items.length === 30 ? '+' : ''})
                </div>
                <ul className="pbnew-move-preview-list">
                  {preview.items.map((s) => (
                    <li key={s._id}>
                      {s.story?.title || `Story ${String(s.storyId).slice(-6)}`}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        ) : (
          <>
            <div style={{ fontSize: 13, color: '#3C4A5E', marginBottom: 10 }}>
              {count} stor{count === 1 ? 'y' : 'ies'} — arriving as a Suggestion in the destination.
            </div>
            <div className="pbnew-search" style={{ maxWidth: 'none', marginBottom: 12 }}>
              <input
                autoFocus
                placeholder="Search publisher or bucket"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <div className="pbnew-filter-label">All available targets</div>
            <div className="pbnew-movetree">
              {filteredTree.map(({ client, matchedBuckets }) => {
                const isOpen = expanded.has(client.clientId);
                return (
                  <div key={client.clientId}>
                    <button
                      type="button"
                      className="pbnew-move-client"
                      onClick={() => toggleClient(client.clientId)}
                      aria-expanded={isOpen}
                    >
                      <span className={`pbnew-chev ${isOpen ? 'open' : ''}`}>
                        {isOpen ? <ExpandMoreIcon style={{ fontSize: 16 }} /> : <ChevronRightIcon style={{ fontSize: 16 }} />}
                      </span>
                      <span className="pbnew-chip" style={{ background: chipColor(client.clientName) }}>
                        {initials(client.clientName)}
                      </span>
                      <span className="name" style={{ whiteSpace: 'normal' }}>{client.clientName}</span>
                      <span className="info">
                        {matchedBuckets.length} bucket{matchedBuckets.length === 1 ? '' : 's'}
                      </span>
                    </button>
                    {isOpen && matchedBuckets.map((b) => {
                      const disabled = b._id === currentBucketId;
                      const selected = selectedBucket?._id === b._id;
                      return (
                        <div
                          key={b._id}
                          className={`pbnew-move-folder ${selected ? 'selected' : ''}`}
                          data-disabled={disabled || undefined}
                        >
                          <button
                            type="button"
                            className="pbnew-move-folder-btn"
                            disabled={disabled}
                            title={disabled ? 'This is the current bucket' : `Send to ${b.name}`}
                            onClick={() => setSelectedBucket(b)}
                          >
                            <FolderOutlinedIcon style={{ fontSize: 16 }} />
                            <span className="pbnew-move-folder-name">{b.name}</span>
                            <span className="count">{activeSuggestions(b)}</span>
                          </button>
                          {!disabled && (
                            <button
                              type="button"
                              className="pbnew-move-folder-preview"
                              onClick={() => openPreview(b)}
                              title="Preview this bucket without leaving"
                            >
                              <VisibilityOutlinedIcon style={{ fontSize: 15 }} />
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
              {/* Loader, not an empty state, while buckets are still loading. */}
              {loading && <BucketsLoading size={120} />}
              {!loading && filteredTree.length === 0 && (
                <div className="pbnew-drop-hint" style={{ padding: 16 }}>No matching destinations.</div>
              )}
            </div>
          </>
        )}
      </div>
    </AppModal>
  );
}
